'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, RotateCcw, AlertCircle } from 'lucide-react';

interface SelfieCaptureProps {
  onCapture: (dataUrl: string) => void;
  /** confirm-button label after capture (defaults to the finder's "Find Photos") */
  confirmLabel?: string;
}

export function SelfieCapture({ onCapture, confirmLabel = 'Find Photos' }: SelfieCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access to find your photos.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Unable to access camera. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      stream?.getTracks().forEach(track => track.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCountdown = () => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Mirror the image (selfie mode)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCaptured(dataUrl);

    // Stop camera
    stream?.getTracks().forEach(track => track.stop());
  };

  const retake = () => {
    setCaptured(null);
    setCountdown(null);
    startCamera();
  };

  const confirm = () => {
    if (captured) onCapture(captured);
  };

  if (cameraError) {
    return (
      <div style={{ textAlign: 'center', padding: '32px' }}>
        <AlertCircle size={40} color="#EF4444" style={{ margin: '0 auto 12px' }} />
        <p style={{ fontSize: '14px', color: '#EF4444', margin: '0 0 16px' }}>{cameraError}</p>
        <button
          onClick={startCamera}
          style={{
            padding: '10px 24px', borderRadius: '8px',
            background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)',
            border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: '400px', borderRadius: '12px', overflow: 'hidden', background: '#000' }}>
        {!captured ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', display: 'block', transform: 'scaleX(-1)' }}
            />
            {countdown !== null && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.3)',
              }}>
                <span style={{ fontSize: '72px', fontWeight: 700, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                  {countdown}
                </span>
              </div>
            )}
          </>
        ) : (
          <img src={captured} alt="Selfie preview" style={{ width: '100%', display: 'block' }} />
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {!captured ? (
        <button
          onClick={startCountdown}
          disabled={!cameraReady || countdown !== null}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '14px 32px', borderRadius: '50px',
            background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)',
            border: 'none', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
            opacity: (!cameraReady || countdown !== null) ? 0.5 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          <Camera size={20} />
          {countdown !== null ? 'Get Ready...' : 'Take Selfie'}
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '12px', width: '100%', maxWidth: '320px' }}>
          <button
            onClick={retake}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              height: '48px', flex: '0 0 auto', padding: '0 20px', borderRadius: '10px',
              background: 'transparent', color: 'var(--fv-text-secondary)',
              border: '1px solid var(--fv-border)', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            <RotateCcw size={16} /> Retake
          </button>
          <button
            onClick={confirm}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '48px', flex: 1, borderRadius: '10px',
              background: 'var(--fv-accent, #006D6D)', color: 'var(--fv-accent-text, #fff)',
              border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      )}

      <p style={{ fontSize: '12px', color: 'var(--fv-text-secondary)', textAlign: 'center', maxWidth: '300px' }}>
        Your selfie is used only to match your face with event photos, and is automatically deleted within 24 hours.
      </p>
    </div>
  );
}
